// ─────────────────────────────────────────────────────────────────────────────
// A FULL SAMPLE BUILDING.
//
// Six hand-built residents are enough to show what a finding looks like. They
// are not enough to show what a FACILITY looks like — a queue you scroll, a
// risk distribution with a shape to it, categories in proportion, and the
// ordinary majority of residents who have nothing wrong. A demo on six
// residents invites "what happens at two hundred?", and the honest answer has
// to be on screen rather than described.
//
// So this generates the rest of the building around them.
//
// ── WHAT IS AND IS NOT INVENTED ────────────────────────────────────────────
//
// Names, rooms, payers, dates, narrative wording: generated.
//
// DOLLAR FIGURES ARE NOT. Every priced finding here reuses an item-change the
// FY 2026 PDPM grouper actually produced a delta for — the same six the
// hand-built set carries, with the same figure and the same basis sentence.
// GG0130A 05 → 02 is $67.70 here because it is $67.70 there, out of the real
// tables. Nothing in this file makes up a number, because a demo figure that
// cannot be defended is worse than no demo at all, and somebody will ask.
//
// Findings whose item has no verified delta are NOT PRICED, with a basis that
// says why. That is a real state the product has, and showing it across a
// whole building is more honest — and more persuasive — than a roster where
// every resident happens to be worth money.
//
// ── DETERMINISTIC ──────────────────────────────────────────────────────────
//
// One fixed seed. The same building every load, every browser, every demo, so
// a figure quoted on a call is the same figure on the next one and a test can
// assert against it. Dates are relative to today so the set never goes stale.
// ─────────────────────────────────────────────────────────────────────────────

const SEED = 0x5c0ffee;

// The items a resident admitted this week plausibly supports with no MDS on
// file yet: eating, transfer, wound, IV antibiotic, weight loss and IV
// hydration. Chosen because each is something a chart shows from day one and
// an MDS has not yet captured — not because they happen to price well.
//
// `o0400` WAS IN THIS SET AND HAD TO COME OUT. Its finding is "therapy minutes
// not reconciled to the MDS", and on a resident with no MDS there is nothing
// to reconcile against — the card read as a coherent sentence while describing
// a comparison that cannot exist. `ivf` replaces it, and is a better fit for
// the opposite reason: its template was already written for a resident with no
// completed assessment, down to the evidence row that says so.
const NEW_ADMISSION_ITEMS = new Set([
  "gg0130a", "gg0170e", "m0300c1", "o0110h1b", "k0300", "ivf",
]);

// WHICH resident. One index, so the same person is the new admission on every
// load and a demo can be rehearsed. Their payer and length of stay are
// assigned from this rather than drawn — see the note at the draw.
const FRESH_ADMISSION_INDEX = 7;

// mulberry32 — small, fast, and stable across engines, which is the whole
// requirement. Not cryptographic and does not need to be.
function rng(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const day = (n) => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const md = (n) => { const d = day(n); return `${d.slice(5, 7)}/${d.slice(8, 10)}`; };

const FIRST = [
  "Alma", "Beatriz", "Cornelius", "Delphine", "Emmett", "Fiona", "Gerald", "Harriet",
  "Ignacio", "Josephine", "Kwame", "Lorraine", "Mable", "Nadia", "Otis", "Priscilla",
  "Quentin", "Rosalind", "Stanley", "Theodora", "Ulises", "Vernon", "Winifred", "Xiomara",
  "Yusuf", "Zelda", "Amara", "Bernard", "Clementine", "Desmond", "Estelle", "Franklin",
  "Genevieve", "Horace", "Imogen", "Jarrell", "Katarzyna", "Leonard", "Maureen", "Nikolai",
  "Ophelia", "Percival", "Rosario", "Solomon", "Tabitha", "Ursula", "Viktor", "Wilhelmina",
];
const LAST = [
  "Achebe", "Bhattacharya", "Castellanos", "Dubois", "Eriksson", "Fitzgerald", "Gallardo",
  "Hollingsworth", "Ibarra", "Jankowski", "Kowalczyk", "Lindqvist", "Mwangi", "Nakamura",
  "Okonkwo", "Pemberton", "Quintanilla", "Rasmussen", "Sotomayor", "Thibodeaux", "Ueda",
  "Vasquez", "Whitmore", "Yamamoto", "Zielinski", "Ferreira", "Haddad", "Novak",
];
const UNITS = ["Rehab A", "Rehab B", "Long-term 1", "Long-term 2", "Memory Care"];

// Weighted so the building looks like a building: roughly a third skilled, a
// third Medicaid long-stay, the rest split. A roster that is all Medicare A
// reads as a sales fantasy to anyone who runs one.
const PAYERS = [
  ["Medicare A", 34], ["Medicaid", 31], ["Managed Care", 18],
  ["Private", 10], ["Medicare B", 7],
];

// ── VERIFIED FINDING TEMPLATES ──────────────────────────────────────────────
//
// perDiem values and basis sentences are lifted from the hand-built set, which
// took them from the grouper. `perDiem: null` is a finding the grouper refused,
// and the basis says why — those are as real as the priced ones.
//
// ── AND EVERY ONE CARRIES WHAT TO DO ABOUT IT ───────────────────────────────
//
// `documentOn`, `codeOn`, `ownerRole`, `documentBy` and `rationale` are what
// the card's "To capture this" panel and its Why line render. The panel is
// gated on documentOn || codeOn, deliberately — an invented "document this
// somewhere" is worse than silence — and the generator set none of them. So
// the six hand-built residents showed a finding with a form, an owner and a
// deadline, and the other sixty-two showed a finding and stopped, which is the
// research task the panel exists to remove. A demo that opens on the six and
// then scrolls into the rest demonstrates the product getting thinner.
//
// Each is written to the item rather than templated: where the chart already
// supports the code, `documentOn` says so plainly instead of inventing
// paperwork, and `documentBy` says what the deadline actually is — an ARD for
// most, day 3 of the stay for NTA, and already-passed for the fall.
const TEMPLATES = [
  {
    key: "gg0130a", category: "ADL", mdsSection: "GG0130", fTags: [676, 692], weight: 16,
    subtype: "Eating — GG0130A",
    currentValue: "GG0130A = 05 (setup assistance)",
    proposedValue: "GG0130A = 02 (substantial/maximal assist)",
    perDiem: 67.70,
    basis: "Grouped as charted, grouped again with GG0130A = 02, and subtracted — FY 2026 PDPM tables. PT TD → TC −$2.27; OT TD → TC +$10.57; Nursing PA1 → PBC1 +$59.40. Net +$67.70.",
    documentOn: "The CNA point-of-care flowsheet for eating, across the assessment look-back. Section GG is coded from usual performance, not the best day — so the flowsheet has to show the look-back, not one entry.",
    codeOn: "GG0130A = 02 on the next scheduled assessment.",
    ownerRole: "MDS coordinator, with SLP to confirm the level of assist",
    documentBy: "The assessment reference date. Section GG is coded from the look-back, so an entry dated after the ARD cannot be used on that assessment.",
    rationale: "The point-of-care flowsheet records substantial/maximal assistance on most days of the look-back, and the speech therapy note describes hand-over-hand assistance throughout the full meal. The locked assessment codes 05, which means the resident feeds themselves once food is placed in front of them.",
    partAOnly: true,
    evidence: (n, who) => [
      { source: "ADL", document: "CNA Point-of-Care Flowsheet — Eating", date: day(0),
        fields: [
          { label: "Look-back", value: `${md(-6)} – ${md(0)} · 7 days` },
          { label: "Substantial/maximal", value: `${4 + (n % 3)} days` },
          { label: "Usual performance", value: "Substantial/maximal assist" },
        ],
        text: "Section GG is coded from usual performance across the look-back, not the best day." },
      { source: "DOCUMENT", document: "Speech Therapy Progress Note", date: day(-(6 + (n % 5))), author: who.slp,
        fields: [{ label: "Assist provided", value: "Verbal cueing + hand-over-hand" }, { label: "Portion of meal", value: "Full meal" }],
        text: "Requires hand-over-hand assistance to bring utensil to mouth throughout the meal, with continuous verbal cueing." },
      { source: "MDS", document: "Nursing home: comprehensive assessment", date: day(-(14 + (n % 12))), author: "Locked", conflict: true,
        fields: [{ label: "GG0130A", value: "05  (setup or clean-up assistance)" }, { label: "Item wording", value: "Eating — usual performance" }],
        text: "Code 05 means the resident feeds themselves once food is in front of them, which the flowsheet contradicts." },
    ],
  },
  {
    key: "gg0170e", category: "ADL", mdsSection: "GG0170", fTags: [677], weight: 8,
    subtype: "Chair/bed transfer — GG0170E",
    currentValue: "not yet coded — no MDS on file",
    proposedValue: "GG0170E = 03 (partial/moderate assist)",
    perDiem: 8.30,
    basis: "Grouped against the FY 2026 PDPM tables with GG0170E = 03 applied to the function score. +$8.30 per diem. This resident has no coded MDS, so the figure is what the 5-Day would capture if coded from the record as it stands — not a gap against something already submitted.",
    documentOn: "Nothing further. The 48-hour ADL tool and the OT evaluation are both on file and both record the transfer level — what is missing is the assessment, not the documentation.",
    codeOn: "GG0170E = 03 on the 5-Day PPS, once it is completed.",
    ownerRole: "MDS coordinator, with OT",
    documentBy: "The 5-Day PPS assessment reference date — day 1 through day 8 of the stay.",
    rationale: "There is no completed MDS for this stay, so this is not a gap against anything submitted. The 48-hour ADL tool and the OT evaluation both record partial/moderate assistance for chair-to-bed transfer, which is what the 5-Day should capture if it is coded from the record as it stands.",
    partAOnly: true, needsNoMds: true,
    evidence: (n, who) => [
      { source: "ASSESSMENT", document: "48-Hour ADL Tool", date: day(-(2 + (n % 3))), author: who.rn,
        fields: [{ label: "Chair/bed transfer", value: "Contact guard assist, one person" }, { label: "Device", value: "Gait belt" }],
        text: "Transfers chair to bed with contact guard assist of one, gait belt in place, requires steadying throughout." },
      { source: "DOCUMENT", document: "Occupational Therapy Evaluation", date: day(-(1 + (n % 3))), author: who.ot,
        fields: [{ label: "Chair/bed transfer", value: "Partial/moderate assistance" }, { label: "Effort by resident", value: "More than half" }],
        text: "Requires partial assistance for chair-to-bed transfer with gait belt. Resident completes more than half the effort." },
      { source: "MDS", document: "No MDS on file for this stay", date: null, missing: true,
        fields: [{ label: "Searched", value: "All assessments, this stay" }, { label: "Found", value: "None — 5-Day PPS not yet completed" }],
        text: "Nothing here is a gap against a submitted assessment. It is what the 5-Day should capture if coded from the record as it stands." },
    ],
  },
  {
    key: "m0300d1", category: "WOUND", mdsSection: "M0300", fTags: [686], weight: 12,
    subtype: "Stage 4 pressure ulcer — sacrum",
    currentValue: "M0300C1 = 1 (one Stage 3)",
    proposedValue: "M0300D1 = 1 (one Stage 4)",
    perDiem: 68.73,
    basis: "Grouped as charted, grouped again with M0300D1 = 1, and subtracted — FY 2026 PDPM tables. Stage 4 is an NTA comorbidity; Stage 3 is not. NTA NF → NE, +$68.73 per diem.",
    documentOn: "The staging field on the weekly skin and wound assessment. The narrative on that same form already describes exposed bone — it is the coded stage beside it that has not moved.",
    codeOn: "M0300D1 = 1, with M0300C1 reduced accordingly, on the next scheduled assessment.",
    ownerRole: "Wound nurse to re-stage; MDS coordinator to code",
    documentBy: "The next assessment reference date. A re-stage dated after the ARD cannot be coded on that assessment.",
    rationale: "Exposed bone at the wound base is Stage 4 by definition, and the wound assessment narrative records it. The staging dropdown on the same form has not changed since admission, so the coded stage and the description of the wound disagree. Only an unhealed Stage 4 is an NTA comorbidity under PDPM.",
    partAOnly: true,
    evidence: (n, who) => [
      { source: "ASSESSMENT", document: "Weekly Skin & Wound Assessment", date: day(-(4 + (n % 6))), author: who.wound,
        fields: [
          { label: "Location", value: "Sacrum" },
          { label: "Measurements", value: `${(3.4 + (n % 9) * 0.2).toFixed(1)} × ${(2.6 + (n % 5) * 0.2).toFixed(1)} × ${(1.4 + (n % 4) * 0.2).toFixed(1)} cm` },
          { label: "Wound bed", value: "Exposed bone" },
        ],
        text: "Sacral wound with exposed bone at the wound base. Peri-wound intact. Continue daily dressing, offloading in place." },
      { source: "ASSESSMENT", document: "Weekly Skin & Wound Assessment — staging field", date: day(-(4 + (n % 6))), author: "Same form, coded section", conflict: true,
        fields: [{ label: "Stage (coded)", value: "Stage 3" }, { label: "Last changed", value: `${md(-(30 + (n % 20)))} (admission)` }],
        text: "The narrative on this form describes exposed bone, which is Stage 4 by definition. The staging dropdown has not changed since admission." },
      { source: "TAR", document: "Treatment Administration Record", date: day(0),
        fields: [{ label: "Order", value: "Daily dressing change — sacral wound" }, { label: "Look-back", value: `${md(-7)} – ${md(0)} · 8 of 8 days` }] },
      { source: "MDS", document: "Nursing home: comprehensive assessment", date: day(-(15 + (n % 14))), author: "Locked", conflict: true,
        fields: [{ label: "M0300C1", value: "1  (one Stage 3)" }, { label: "M0300D1", value: "0  (no Stage 4)" }],
        text: "Only an unhealed Stage 4 pressure ulcer is an NTA comorbidity under PDPM. A Stage 3 is not." },
    ],
  },
  {
    key: "m0300c1", category: "WOUND", mdsSection: "M0300", fTags: [686], weight: 7,
    subtype: "Stage 3 pressure ulcer — right heel",
    currentValue: "M0300C1 = 0",
    proposedValue: "M0300C1 = 1",
    perDiem: null,
    basis: "Not priced — a Stage 3 pressure ulcer is not an NTA comorbidity under PDPM, so coding it moves no case-mix group. It is a real clinical and survey finding (F686) and is shown for that reason, not because it pays.",
    documentOn: "Nothing is missing. The wound assessment records full-thickness loss with subcutaneous fat visible and no exposed bone, tendon or muscle, which is Stage 3 — the item is simply not coded.",
    codeOn: "M0300C1 = 1 on the next scheduled assessment.",
    ownerRole: "MDS coordinator",
    documentBy: "The next assessment reference date.",
    rationale: "Full-thickness tissue loss with subcutaneous fat visible and no exposed bone, tendon or muscle is a Stage 3 pressure ulcer, and it was acquired in house. It moves no case-mix group, so it is here as a record and survey finding rather than a capture — the F686 exposure is the reason to code it.",
    evidence: (n, who) => [
      { source: "ASSESSMENT", document: "Weekly Skin & Wound Assessment", date: day(-(6 + (n % 8))), author: who.wound,
        fields: [
          { label: "Location", value: "Right heel" },
          { label: "Tissue loss", value: "Full thickness — subcutaneous visible" },
          { label: "Bone / tendon", value: "NOT exposed" },
          { label: "Present on admission", value: "No — acquired in house" },
        ],
        text: "Full-thickness tissue loss to the right heel with subcutaneous fat visible. No exposed bone, tendon or muscle. Offloading boot in place." },
      { source: "TAR", document: "Treatment Administration Record", date: day(0),
        fields: [{ label: "Order", value: "Daily dressing — right heel" }, { label: "Offloading", value: "Pressure-relief boot, continuous" }] },
      { source: "MDS", document: "Quarterly assessment", date: day(-(20 + (n % 20))), author: "Locked", conflict: true,
        fields: [{ label: "M0300C1", value: "0  (no Stage 3)" }] },
    ],
  },
  {
    key: "o0110h1b", category: "ANTIBIOTIC", mdsSection: "O0110", fTags: [880], weight: 12,
    subtype: "IV antibiotic — vancomycin",
    currentValue: "O0110H1b unchecked",
    proposedValue: "O0110H1b checked",
    perDiem: 173.28,
    basis: "Grouped as charted, grouped again with O0110H1B checked, and subtracted — FY 2026 PDPM tables. NTA comorbidity score moves the resident NF → ND, +$173.28 per diem. NTA pays three times the rate on days 1–3.",
    documentOn: "Nothing. The physician order, the MAR and the nursing note are all on file. This is a change in condition after the last assessment reference date, not a documentation gap.",
    codeOn: "O0110H1b on the next assessment — and decide whether an Interim Payment Assessment is warranted, since the next scheduled one may be weeks away.",
    ownerRole: "MDS coordinator",
    documentBy: "While the antibiotic is still running, so the look-back covers it. NTA pays three times the base rate only through day 3 of the stay, so the window for its full value is short.",
    rationale: "An IV antibiotic is running under an active order and the MAR shows every scheduled dose given. The locked 5-Day was correct on its own ARD — the antibiotic started after it — so the item is unchecked on the only assessment on file, and nothing will change that except a new assessment.",
    partAOnly: true,
    evidence: (n, who) => [
      { source: "ORDER", document: "Physician Order", date: day(-(3 + (n % 4))), author: who.md,
        fields: [
          { label: "Medication", value: "Vancomycin 1 g" },
          { label: "Route", value: "IV (PICC, left upper arm)" },
          { label: "Frequency", value: "q12h" },
          { label: "Indication", value: n % 2 ? "MRSA bacteremia" : "Osteomyelitis" },
        ],
        text: "Vancomycin 1 g IV every 12 hours x 14 days. Trough prior to 4th dose. Infectious disease following." },
      { source: "MAR", document: "Medication Administration Record", date: day(0),
        fields: [{ label: "Look-back", value: `${md(-6)} – ${md(0)}` }, { label: "Doses given", value: `${10 + (n % 3)} of ${10 + (n % 3)} scheduled` }] },
      { source: "NOTE", document: "Nursing Progress Note", date: day(-(2 + (n % 3))), author: who.rn,
        text: "PICC to left upper arm. Dressing clean, dry and intact, no erythema at the site. Antibiotic infusing per order." },
      { source: "MDS", document: "5-Day PPS", date: day(-(5 + (n % 4))), author: "Locked", conflict: true,
        fields: [{ label: "O0110H1b", value: "NOT CHECKED" }, { label: "Item wording", value: "IV medications — while a resident" }],
        text: "Correct as of its ARD — the antibiotic started after it. A change in condition after the assessment, not a coding error." },
    ],
  },
  {
    key: "k0300", category: "CONSULT", mdsSection: "K0300", fTags: [692], weight: 14,
    subtype: "Significant weight loss — dietitian",
    currentValue: "K0300 = 0 (no weight loss)",
    proposedValue: "K0300 = 2 (loss, NOT on a prescribed regimen)",
    perDiem: 67.70,
    basis: "Grouped as charted, grouped again with K0300 = 2, and subtracted — FY 2026 PDPM tables. Moves the SLP component and contributes to the Nursing category. +$67.70 per diem.",
    documentOn: "Nothing further. Two recorded weights thirty days apart and the dietitian's note that there is no prescribed weight-reduction regimen are both on file, which is exactly what K0300 is coded from.",
    codeOn: "K0300 = 2 on the next scheduled assessment.",
    ownerRole: "MDS coordinator, with the registered dietitian",
    documentBy: "The next assessment reference date.",
    rationale: "Two recorded weights thirty days apart show a loss above the 5% threshold K0300 is coded from, and the dietitian's note answers the one question that separates K0300 = 1 from = 2: the resident is not on a physician-prescribed weight-reduction regimen.",
    partAOnly: true,
    evidence: (n, who) => {
      const from = 142 + (n % 40);
      const pct = (5.2 + (n % 9) * 0.3).toFixed(1);
      const to = (from * (1 - pct / 100)).toFixed(1);
      return [
        { source: "VITALS", document: "Weight Record", date: day(-(7 + (n % 6))),
          fields: [
            { label: `Weight ${md(-(37 + (n % 6)))}`, value: `${from}.0 lb` },
            { label: `Weight ${md(-(7 + (n % 6)))}`, value: `${to} lb` },
            { label: "Percent", value: `${pct}%  (K0300 threshold is 5% / 30 days)` },
          ],
          text: "Both weights are recorded thirty days apart. K0300 requires the threshold to be evidenced by recorded weights." },
        { source: "DOCUMENT", document: "Registered Dietitian Nutrition Note", date: day(-(8 + (n % 7))), author: who.rd,
          fields: [
            { label: "Intake", value: "25–50% at most meals" },
            { label: "Prescribed weight-loss regimen", value: "NO" },
          ],
          text: "Significant unplanned weight loss. Resident is not on a physician-prescribed weight-reduction regimen." },
        { source: "MDS", document: "Quarterly assessment", date: day(-(18 + (n % 16))), author: "Locked", conflict: true,
          fields: [{ label: "K0300", value: "0  (no weight loss)" }],
          text: "The answer separating K0300 = 1 from = 2 is whether the loss was on a prescribed regimen. The dietitian note answers it: no." },
      ];
    },
  },
  {
    key: "k0510d2", category: "CONSULT", mdsSection: "K0520", fTags: [692], weight: 6,
    subtype: "Therapeutic diet not coded",
    currentValue: "K0520D3 unchecked",
    proposedValue: "K0520D3 checked (therapeutic diet, while a resident)",
    perDiem: 0,
    basis: "Priced against the FY 2026 PDPM tables: coding this moves no case-mix group for this resident, so there is no per-diem effect. It is shown because the documentation gap is real and carries survey exposure, not because it pays.",
    documentOn: "A dietary entry showing the ordered diet was actually served during the look-back. The order is active and the tray card matches it; the service record is the part K0520 is coded from and the part that is absent.",
    codeOn: "K0520D3 once the look-back carries the service record — not before it.",
    ownerRole: "Dietary manager to document service; MDS coordinator to code",
    documentBy: "Before the next assessment reference date, and as it happens. A diet service record reconstructed afterwards is the entry a surveyor asks about.",
    rationale: "An active therapeutic diet order exists and the tray card matches it, but there is no entry anywhere in the look-back showing the diet was served. K0520 is coded from the service record rather than from the order, so the order alone does not support the item.",
    evidence: (n, who) => [
      { source: "ORDER", document: "Physician Diet Order", date: day(-(10 + (n % 8))), author: who.md,
        fields: [{ label: "Diet", value: n % 2 ? "2 g sodium, renal" : "Carbohydrate-controlled" }, { label: "Status", value: "Active" }] },
      { source: "ASSESSMENT", document: "Dietary implementation entry", date: null, missing: true,
        fields: [{ label: "Searched", value: `${md(-(10 + (n % 8)))} – ${md(0)}` }, { label: "Found", value: "None" }],
        text: "The order exists and the tray card matches it. What is absent is any entry showing the diet was served during the look-back, which is what K0520 is coded from." },
      { source: "MDS", document: "Quarterly assessment", date: day(-(16 + (n % 18))), author: "Locked", conflict: true,
        fields: [{ label: "K0520D3", value: "NOT CHECKED" }] },
    ],
  },
  {
    key: "j1800", category: "CONSULT", mdsSection: "J1800", fTags: [689, 657], weight: 6,
    subtype: "Post-fall neuro checks not documented",
    currentValue: "J1800 = 0 (no falls since admission/prior assessment)",
    proposedValue: "J1800 = 1, with J1900 coded to injury level",
    perDiem: null,
    basis: "Not priced — the missing documentation is the finding, and there is no completed assessment of the injury level to group against. This is survey exposure (F689), not a coding capture.",
    documentOn: "What was actually done and observed after the fall, and why no neuro-check series was recorded. The 72-hour window has closed, so this is a late entry describing real events — not a series back-filled to fill the hole.",
    codeOn: "J1800 = 1, and J1900 at the assessed injury level, on the next scheduled assessment.",
    ownerRole: "Charge nurse for the late entry; care-plan team for the revision; MDS coordinator to code",
    documentBy: "The neuro-check window has already passed. Document now — and do not create a series that did not happen.",
    rationale: "An incident report records an unwitnessed fall with a suspected head strike. There is no neurological check series, no new physician order and no care-plan revision in the 72 hours after it. The absence is the finding: coding J1800 without it leaves the record asserting a fall the facility's own post-fall protocol has no trace of.",
    evidence: (n, who) => [
      { source: "DOCUMENT", document: "Incident / Accident Report", date: day(-(9 + (n % 10))), author: who.rn,
        fields: [
          { label: "Type", value: "Fall — unwitnessed" },
          { label: "Location", value: "Resident's room, beside bed" },
          { label: "Head strike", value: "Suspected — resident unable to recall" },
        ],
        text: "Resident found on the floor beside the bed. Unable to state whether the head was struck. Physician and family notified." },
      { source: "ASSESSMENT", document: "Post-fall neurological check series", date: null, missing: true,
        fields: [
          { label: "Searched", value: `${md(-(9 + (n % 10)))} – ${md(-(6 + (n % 10)))} · 72 hours` },
          { label: "Found", value: "None" },
        ],
        text: "The absence is the finding. An unwitnessed fall with a suspected head strike requires the neuro-check series, and there is no record of one." },
      { source: "MDS", document: "Quarterly assessment", date: day(-(22 + (n % 14))), author: "Locked", conflict: true,
        fields: [{ label: "J1800", value: "0  (no falls since admission / prior assessment)" }] },
    ],
  },
  {
    key: "o0400", category: "THERAPY", mdsSection: "O0400", fTags: [676], weight: 5,
    subtype: "Therapy minutes not reconciled to the MDS",
    currentValue: "O0400 therapy minutes as coded",
    proposedValue: "O0400 reconciled to the treatment log",
    perDiem: null,
    basis: "Not priced — under PDPM therapy minutes do not drive payment, so no case-mix group moves. The reconciliation matters for the medical record and for an ADR, which is why it is surfaced.",
    documentOn: "Nothing — the therapy treatment log is signed and complete. The work is reconciling the log against the coded total and recording which one was wrong.",
    codeOn: "O0400 reconciled to the treatment log on the next assessment, and a modification to the submitted assessment if the coded total is the one in error.",
    ownerRole: "Rehab director to confirm the minutes; MDS coordinator to reconcile",
    documentBy: "No payment deadline — under PDPM the minutes do not drive payment. The deadline is whenever the record is read: an ADR, a medical review, or a survey.",
    rationale: "The signed therapy treatment log and the coded O0400 total disagree. Under PDPM therapy minutes do not drive payment, so no case-mix group moves and there is nothing to capture. What is exposed is the medical record, which is what an additional documentation request reads.",
    evidence: (n, who) => [
      { source: "DOCUMENT", document: "Therapy Treatment Log", date: day(-(3 + (n % 5))), author: who.ot,
        fields: [
          { label: "Discipline", value: n % 2 ? "Physical therapy" : "Occupational therapy" },
          { label: "Minutes logged", value: `${230 + (n % 7) * 15} over 7 days` },
        ],
        text: "Individual treatment minutes recorded per session, signed by the treating therapist." },
      { source: "MDS", document: "Most recent assessment", date: day(-(12 + (n % 16))), author: "Locked", conflict: true,
        fields: [{ label: "O0400 total", value: `${180 + (n % 5) * 15} minutes` }],
        text: "The coded total and the treatment log disagree. Under PDPM this is a record-accuracy issue, not a payment one." },
    ],
  },
  {
    key: "ivf", category: "IVF", mdsSection: "O0110", fTags: [692, 684], weight: 4,
    subtype: "IV fluids — hydration",
    currentValue: "O0110H1a unchecked",
    proposedValue: "O0110H1a checked (IV fluids, while a resident)",
    perDiem: null,
    basis: "Not priced — the grouper has no completed MDS for this resident to group against, so no baseline exists to subtract from. The documentation gap is real; the dollar figure is not claimed.",
    documentOn: "Nothing. The physician order and the nursing note are both on file, including the site assessment.",
    codeOn: "O0110H1a on the first completed assessment for this stay.",
    ownerRole: "MDS coordinator",
    documentBy: "While the infusion is still running, so the assessment look-back covers it.",
    rationale: "IV hydration is running under an active physician order for poor oral intake, with a nursing note recording the site and the continuing poor intake. There is no completed assessment on file for this stay, so there is no baseline to group against and no dollar figure is claimed.",
    evidence: (n, who) => [
      { source: "ORDER", document: "Physician Order", date: day(-(2 + (n % 5))), author: who.md,
        fields: [{ label: "Fluid", value: "0.9% NaCl 1000 mL" }, { label: "Rate", value: "75 mL/hr" }, { label: "Indication", value: "Dehydration" }],
        text: "IV hydration for poor oral intake. Reassess daily, discontinue when intake improves." },
      { source: "NOTE", document: "Nursing Progress Note", date: day(-(1 + (n % 4))), author: who.rn,
        text: "IV infusing via right forearm, site without redness or swelling. Oral intake remains poor at 25%." },
      { source: "MDS", document: "No completed assessment to compare against", date: null, missing: true,
        fields: [{ label: "Searched", value: "All assessments on file" }, { label: "Found", value: "None completed" }] },
    ],
  },
];

const RISK_REASONS = {
  gg0130a: "Functional decline evidenced across two dated readings",
  gg0170e: "No MDS on file — 5-Day PPS window open",
  m0300d1: "Stage 4 pressure ulcer, unhealed",
  m0300c1: "Facility-acquired Stage 3 pressure ulcer",
  o0110h1b: "Active IV antibiotic therapy",
  k0300: "Documented significant weight loss over 30 days",
  k0510d2: "Therapeutic diet ordered without documented implementation",
  j1800: "Unwitnessed fall with suspected head strike",
  o0400: "Therapy minutes disagree with the coded assessment",
  ivf: "IV hydration in progress for poor oral intake",
};
const FLAG_FOR = {
  ADL: "📉", WOUND: "🩹", ANTIBIOTIC: "💊", CONSULT: "🍽️", THERAPY: "🏃", IVF: "💧", OXYGEN: "🫁",
};
const BASE_REASONS = [
  "Polypharmacy — 12 or more active orders",
  "Two hospitalisations in the last six months",
  "Admitted within the last 14 days",
  "Dementia diagnosis with wandering risk",
  "Anticoagulant therapy",
  "History of recurrent UTI",
];

// ── THE EVIDENCE PANEL CANNOT CITE AN ASSESSMENT THAT DOES NOT EXIST ────────
//
// Four of the six templates the fresh admission uses end with an MDS row
// marked `conflict: true` — "Quarterly assessment · Locked · M0300C1 = 0",
// "5-Day PPS · Locked · O0110H1b NOT CHECKED". For every other resident that
// row IS the finding's argument: here is the coded value, and here is the
// chart disagreeing with it. On a resident with no completed MDS it is a
// fabricated document in the one panel the screen presents as proof, under a
// card whose own headline says no MDS is on file.
//
// So the MDS row is dropped and the one true row put in its place, copied in
// shape from gg0170e's, which was written for this case. It is `missing: true`,
// so it counts as nothing toward the evidence total rather than padding it.
function noMdsEvidence(rows) {
  return [
    ...rows.filter((e) => e.source !== "MDS"),
    {
      source: "MDS", document: "No MDS on file for this stay", date: null, missing: true,
      fields: [
        { label: "Searched", value: "All assessments, this stay" },
        { label: "Found", value: "None — 5-Day PPS not yet completed" },
      ],
      text: "Nothing here is a gap against a submitted assessment. It is what the 5-Day should capture if it is coded from the record as it stands.",
    },
  ];
}

function pickWeighted(r, table) {
  const total = table.reduce((n, [, w]) => n + w, 0);
  let x = r() * total;
  for (const [v, w] of table) { if ((x -= w) <= 0) return v; }
  return table[table.length - 1][0];
}

/**
 * The rest of the building.
 *
 * @param {number} count how many to generate (the hand-built six sit alongside)
 * @param {number} startIndex so ids never collide with the hand-built set
 */
export function buildSampleRoster(count = 74, startIndex = 100) {
  const r = rng(SEED);
  const residents = [];
  const opportunities = [];
  const usedNames = new Set();

  for (let i = 0; i < count; i += 1) {
    const idx = startIndex + i;
    const id = `demo-r${idx}`;

    let name;
    for (let guard = 0; guard < 200; guard += 1) {
      const candidate = `${FIRST[Math.floor(r() * FIRST.length)]} ${LAST[Math.floor(r() * LAST.length)]}`;
      if (!usedNames.has(candidate)) { name = candidate; usedNames.add(candidate); break; }
    }
    if (!name) name = `Resident ${idx}`;

    const staff = {
      rn: `${["J. Castellanos", "P. Nwosu", "D. Ferreira", "R. Idowu"][i % 4]}, RN`,
      md: `${["M. Okonkwo", "S. Whitcomb", "A. Bergstrom"][i % 3]}, MD`,
      rd: `${["L. Moreau", "T. Vance"][i % 2]}, RD`,
      slp: `${["T. Brennan", "K. Osei"][i % 2]}, MS CCC-SLP`,
      ot: `${["A. Sandoval", "M. Lindqvist"][i % 2]}, OTR/L`,
      wound: `${["K. Alvarez", "N. Pereira"][i % 2]}, RN — Wound Nurse`,
    };

    // ── THE FRESH ADMISSION IS CHOSEN, NOT ROLLED ───────────────────────────
    //
    // The first attempt at this was `onPartA && i === 7`, and resident 7 drew
    // Medicaid — so the condition was false for every resident in the building
    // and the roster came back with ZERO all-blocked residents while reading as
    // though it had one. A fixed index ANDed with a random draw is a condition
    // that compiles, looks deliberate and never fires; the payer has to be
    // assigned rather than tested.
    //
    // pickWeighted is still CALLED, so the random stream is identical for this
    // resident's name, unit and score and for every resident after them. Only
    // the value is replaced.
    const freshAdmission = i === FRESH_ADMISSION_INDEX;
    const rolledPayer = pickWeighted(r, PAYERS);
    const payer = freshAdmission ? "Medicare A" : rolledPayer;
    const onPartA = payer === "Medicare A";
    const rolledLos = onPartA ? 3 + Math.floor(r() * 40) : 30 + Math.floor(r() * 900);
    // Four days in. Short enough that no MDS could plausibly be complete — the
    // 5-day PPS assessment is not due yet, which is exactly why every item is
    // blocked rather than missed.
    const los = freshAdmission ? 4 : rolledLos;

    // HALF THE BUILDING HAS NOTHING WRONG, AND THAT IS THE POINT.
    // A roster where every resident carries a finding is not a facility, it is
    // a pitch. The clean majority is what makes the flagged minority mean
    // something — and it is what a DON recognises as their own building.
    // A skilled resident is under active assessment and is where the coding
    // gaps concentrate; a stable long-stay Medicaid resident mostly is not.
    // Splitting the odds that way is both truer and what makes the roster
    // read as a real census rather than a uniform sprinkle.
    const roll = r();
    const findingCount = onPartA
      ? (roll < 0.18 ? 0 : roll < 0.58 ? 1 : roll < 0.88 ? 2 : 3)
      : (roll < 0.62 ? 0 : roll < 0.90 ? 1 : 2);

    const chosen = [];
    // Weighted, because an unweighted pick over a pool that is mostly
    // unpriceable templates produces a building where almost nothing is worth
    // anything — realistic in shape, useless as a demonstration, and not
    // representative of a facility where someone actually codes.
    const pool = TEMPLATES.filter((t) => !t.partAOnly || onPartA);
    for (let k = 0; k < findingCount && pool.length; k += 1) {
      const t = pickWeighted(r, pool.map((x) => [x, x.weight || 5]));
      if (chosen.some((c) => c.key === t.key)) continue;
      chosen.push(t);
    }

    // ── ONE FRESH ADMISSION, BECAUSE THE DEMO COULD NOT SHOW ITS OWN SCREEN ──
    //
    // A resident admitted days ago has no completed MDS, so EVERY item the
    // chart supports is uncodeable until one exists. In production that is the
    // heaviest card count on the roster — one real resident carried 68 of them
    // — and the opportunities panel now folds the whole list behind one line
    // saying so.
    //
    // The generated roster could never produce it. Findings are drawn 0–3 at a
    // time from a pool where one template of ten is a no-MDS finding, so a
    // resident whose findings are ALL blocked is somewhere around a thousand to
    // one. The collapse is real, shipped, and was invisible in every demo.
    //
    // So one resident is built deliberately rather than rolled. Not to flatter
    // the product: a new admission with nothing coded yet is the most ordinary
    // thing in a building, and it is the state where the queue is least useful
    // until somebody completes the assessment. Showing that honestly is worth
    // more than another resident with a dollar figure.
    if (freshAdmission) {
      chosen.length = 0;
      chosen.push(...pool.filter((t) => NEW_ADMISSION_ITEMS.has(t.key)));
    }

    const fTags = new Set();
    const flags = new Set();
    const reasons = [];
    let priced = 0, notPriced = 0, total = 0;

    // ── NO CLOCK ON A CARD MAY PREDATE THE ADMISSION ────────────────────────
    //
    // Spreading `updated_at` over 26 days put SEVEN cards before their
    // resident arrived — one read "last evaluated 20 days ago" on a resident
    // three days into their stay. Both of a card's clocks are bounded by the
    // length of stay, because there was no chart to read before it, and
    // `detectedAt` has the same ceiling for the same reason.
    const inStay = (n) => day(-Math.max(0, Math.min(n, los - 1)));

    chosen.forEach((t, k) => {
      const oid = `demo-o${idx}-${k}`;
      // THE FRESH ADMISSION'S WINDOW IS ONE WINDOW, NOT SIX. Every item on
      // this resident is waiting on the same 5-day PPS assessment, so they
      // close on the same day — scattering them across a fortnight would show
      // six independent deadlines where there is one, and a coordinator would
      // read the far ones as time they do not have.
      const daysToClose = freshAdmission ? 4 : 1 + Math.floor(r() * 13);
      const badge = daysToClose <= 3 ? "RED" : daysToClose <= 8 ? "AMBER" : "GREEN";
      const ev = freshAdmission
        ? noMdsEvidence(t.evidence(i + k, staff))
        : t.evidence(i + k, staff);
      const realEvidence = ev.filter((e) => !e.missing).length;

      // ONE PER-DIEM, READ BY BOTH THE CARD AND THE RESIDENT ROW.
      //
      // The tally below used to read t.perDiem directly while the card read
      // the nulled value, so the fresh admission's six cards all said "not
      // priced" and the roster row beside them said $316.98 a day. Two sources
      // for one number, disagreeing, with nothing able to report it — which is
      // the defect this file now exists to demonstrate the absence of.
      const perDiem = (freshAdmission && !t.needsNoMds) ? null : t.perDiem;
      if (perDiem == null) notPriced += 1;
      else { priced += 1; total += perDiem; }
      t.fTags.forEach((f) => fTags.add(f));
      flags.add(FLAG_FOR[t.category] || "⚠️");
      reasons.push(RISK_REASONS[t.key]);

      opportunities.push({
        id: oid, residentName: name, resident_id: id,
        room: String(200 + idx), badge,
        category: t.category, subtype: t.subtype, mdsSection: t.mdsSection,
        // ON THE FRESH ADMISSION EVERY CURRENT VALUE IS THE SAME ABSENCE, and
        // it has to SAY so in the words the panel reads — "no MDS" — or the
        // card renders as an ordinary correction against a coded value that
        // does not exist. The template's own currentValue describes a coded
        // MDS, which is exactly what this resident has not got.
        currentValue: freshAdmission
          ? "not yet coded — no MDS on file"
          : t.currentValue,
        proposedValue: freshAdmission
          ? `${t.proposedValue} — once the MDS is completed`
          : t.proposedValue,
        // ── AND THE DOLLAR FIGURE COMES OFF THE FRESH ADMISSION ─────────────
        //
        // Every priced basis here starts "grouped as charted, grouped again
        // with X, and subtracted". On a resident with no completed MDS there
        // is no as-charted grouping to subtract from — the resident has no
        // case-mix classification at all — so an NTA or nursing delta like
        // "NF → ND" is not conservatism to drop, it is a number that cannot be
        // computed. Carrying the template's figure over would have put $316.98
        // a day on the one resident where nothing is capturable yet, under a
        // sentence describing a calculation that did not happen.
        //
        // gg0170e keeps its figure, and it is the only one that can: it is a
        // one-sided function-score grouping and its basis was written for
        // exactly this case. `needsNoMds` is what marks it.
        impactPerDiem: perDiem,
        impactSource: perDiem == null ? null : "grouper",
        impactBasis: (freshAdmission && !t.needsNoMds)
          ? "Not priced — this resident has no completed MDS, so the grouper has no as-charted classification to subtract from. The item is supported by the chart; what it is worth cannot be stated until the 5-Day PPS exists."
          : t.basis,
        // WHAT TO ACTUALLY DO — the card's "To capture this" panel, which is
        // gated on documentOn || codeOn and so rendered nothing on all 62 of
        // these cards until the templates carried it.
        //
        // The fresh admission's answer is a different sentence from everybody
        // else's and cannot be the template's. For every other resident the
        // deadline is the NEXT assessment reference date; for this one there is
        // no assessment at all, so the thing to code against is the 5-Day that
        // has not been completed yet, and the owner is whoever completes it.
        // Printing "on the next scheduled assessment" beside "no MDS on file"
        // is the same contradiction the last-MDS fields had to avoid.
        // documentOn and rationale are the template's EXCEPT on the fresh
        // admission, where several of them name a locked assessment — "the
        // locked 5-Day was correct on its own ARD", "the coded O0400 total" —
        // and there is no assessment to name. One sentence is true of all six
        // cards on that resident, so it is used for all six.
        documentOn: freshAdmission
          ? "Nothing. The chart entries behind this item are already on file — what is missing is the assessment, not the documentation."
          : t.documentOn,
        codeOn: freshAdmission
          ? `${t.proposedValue} — on the 5-Day PPS, once it is completed.`
          : t.codeOn,
        ownerRole: freshAdmission ? "MDS coordinator — the 5-Day PPS is not completed yet" : t.ownerRole,
        documentBy: freshAdmission
          ? "The 5-Day PPS assessment reference date — day 1 through day 8 of the stay. Nothing here can be coded until that assessment exists."
          : t.documentBy,
        rationale: freshAdmission
          ? "The chart supports this item from the day of admission, and there is no completed MDS for this stay to compare it against — so it is not a gap against anything submitted. It is what the 5-Day PPS should capture if it is coded from the record as it stands."
          : t.rationale,
        evidenceCount: realEvidence, evidenceItems: ev,
        daysToClose, status: "OPEN", verification: "CONFIRMED",
        // AND THE "LAST MDS" FIELDS HAVE TO GO EMPTY WITH THEM. These three
        // say which completed assessment the current value was read from, and
        // the template's own flag only covers the ONE template written as a
        // no-MDS finding. On this resident every card is one, so leaving the
        // other five populated would print a locked 5-day PPS from eleven days
        // ago beside the sentence saying no MDS exists — the honest-prose-under-
        // a-wrong-state defect, in a fixture, where it is hardest to notice.
        lastMdsAssessmentDate: (freshAdmission || t.needsNoMds) ? null : day(-(12 + (i % 20))),
        lastMdsAssessmentType: (freshAdmission || t.needsNoMds) ? null : (onPartA ? "5-Day PPS" : "quarterly asmt"),
        lastMdsLocked: (freshAdmission || t.needsNoMds) ? null : true,
        detectedAt: inStay(1 + (i % 5)),
        // WHEN THE CARD WAS LAST EVALUATED, which the panel reads as
        // `updated_at` and prints only once it is three days old or more.
        //
        // The demo set this nowhere, so that line never appeared — and in
        // production it is on most cards, because PointClickCare's approval
        // carries no assessment webhook and nothing pushes us when an MDS is
        // accepted. One real resident's cards read 3 to 43 days. A demo where
        // every card looks freshly checked misrepresents the one thing the
        // panel is careful to admit about itself.
        //
        // Spread so the majority are recent and a minority are visibly stale,
        // which is the shape of a queue somebody is working rather than one
        // nobody has opened.
        //
        // The fresh admission is the exception and has to be: a resident four
        // days in cannot carry a card last evaluated three weeks ago. Theirs
        // run 0–2 days, which is also what the panel should show — nothing
        // stale about a finding raised the day the chart arrived.
        updated_at: inStay(freshAdmission ? k % 3 : (i * 7 + k * 3) % 26),
        nextAssessmentWindowClose: day(daysToClose),
        impactFtags: t.fTags,
      });
    });

    // The score follows the findings rather than being sprinkled on top, so a
    // RED resident always has something on their card explaining it.
    const base = 18 + Math.floor(r() * 22);
    const score = Math.min(97, base + chosen.length * 17 + (chosen.some((t) => t.perDiem > 100) ? 12 : 0));
    const badge = score >= 62 ? "RED" : score >= 38 ? "AMBER" : "GREEN";
    while (reasons.length < 2) {
      const extra = BASE_REASONS[Math.floor(r() * BASE_REASONS.length)];
      if (!reasons.includes(extra)) reasons.push(extra);
    }

    residents.push({
      id, name,
      room: String(200 + idx),
      unit: UNITS[Math.floor(r() * UNITS.length)],
      payer, lengthOfStay: los,
      riskBadge: badge, survaiRiskScore: score,
      openOpportunityCount: chosen.length,
      pdpm_per_diem_usd: Math.round(total * 100) / 100,
      pdpm_not_priced_count: notPriced,
      rehospRiskScore: Math.max(8, score - 10 - Math.floor(r() * 12)),
      rehospRisk: score >= 62 ? "high" : score >= 38 ? "moderate" : "low",
      risk_reasons: reasons.slice(0, 3),
      predictedFtags: [...fTags].map((f) => `F${f}`),
      triggerFlags: flags.size ? [...flags] : ["🟢"],
      // Set so the "how current this is" panel has something true to say.
      enriched_at: new Date(Date.now() - (1 + (i % 9)) * 3600000).toISOString(),
    });
  }

  return { residents, opportunities };
}

export const SAMPLE_ROSTER = buildSampleRoster();
